<template>
  <BaseFormWrapper
    class="c-dropdown-bookmark-mentions"
    :class="{ mobile: isMobile }"
    form-class="full-height column no-wrap"
  >
    <div class="title">
      {{ props.title }}
    </div>

    <div class="sub-title q-mt-sm">{{ subTitle }}</div>

    <div class="q-mt-sm">
      <BaseInputText
        v-model="label"
        data-testid="tag-label"
        new-design
        :placeholder="tagPlaceholder"
      />
    </div>

    <BaseLoadingBar
      v-if="isLoading"
      class="q-mt-sm"
      height="2"
    />

    <div
      v-if="!showSettings"
      class="checkboxes q-mt-sm"
    >
      <BaseCheckbox
        v-for="bookmarkStream in filteredFolders"
        :key="bookmarkStream.id"
        v-model="bookmarkStream.selected"
        :data-testid="`bookmark-stream-checkbox-${bookmarkStream.id}`"
        :disabled="isLoading"
        new-design
        @update:model-value="updateBookmarks(bookmarkStream)"
      >
        <BaseTag
          class="tw:ml-2"
          hide-cursor
          hide-remove-button
          :label="bookmarkStream.label"
          rounded
          :style="bookmarkColor(bookmarkStream)"
        />
      </BaseCheckbox>
    </div>

    <div
      v-if="showCreateButton && !showSettings"
      class="row q-mt-sm flex"
    >
      <BaseButton
        class="create-button"
        data-testid="create-button"
        dense
        icon="plus"
        icon-size="sm"
        :label="getLocaleText('dropdown_bookmark_mentions.create_a_new_tag')"
        new-design
        new-icon
        @click="showCreateSettings"
      />
    </div>

    <template v-if="showSettings">
      <div class="q-mt-md">
        <StreamSettingsGroup
          v-model:group-id="existingGroupId"
          v-model:new-group-color="newGroupColor"
          v-model:new-group-label="newGroupLabel"
          v-model:place-into-new-group="placeIntoNewGroup"
          bookmark-group
          bookmark-mentions
          data-testid="stream-settings-group"
          spacing="lg"
          :stream="props.stream ?? undefined"
        />
      </div>
      <div class="button-container q-mt-md">
        <BaseButton
          data-testid="cancel-button"
          :label="getLocaleText('global.cancel')"
          new-design
          @click="cancel"
        />
        <BaseButton
          :aria-label="getLocaleText('global.save')"
          class="e2e-save"
          color="primary"
          data-testid="save-button"
          :label="getLocaleText('global.create')"
          new-design
          @click="save"
        />
      </div>
    </template>
  </BaseFormWrapper>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import { Notify } from "quasar";
import { computed, inject, ref, watch } from "vue";

import { AlertDialog } from "shared/boot/alert";
import { $streemApiV1 } from "shared/boot/api";
import { getLocaleText } from "shared/boot/i18n";
import {
  BaseButton,
  BaseFormWrapper,
  BaseInputText,
  BaseLoadingBar,
  BaseTag,
} from "shared/components/base";
import BaseCheckbox from "shared/components/core/forms/BaseCheckbox.vue";
import StreamSettingsGroup from "shared/components/streams/StreamSettings/StreamSettingsGroup.vue";
import {
  type AddNewBookmarkStreamPayload,
  type BookmarkStream,
  type BookmarkStreamsForMentionsResponse,
  type MoveToExistingGroupPayload,
} from "shared/composables/useBookmarks";
import { defaultBookmarkPalette, tagPalette } from "shared/constants";
import {
  type CompositeIdToMention,
  compositeIdToMention,
  targetType,
} from "shared/helpers/mentions";
import { isSocialStream } from "shared/helpers/stream";
import { useStreamsStore } from "shared/stores/streams";
import type { Maybe, Nullable, Stream } from "shared/types";
import type { SyndicationKey } from "shared/types/mentions";

interface BookmarkStreamColor {
  fontColor: string;
}

export interface DropdownBookmarkMentionsProps {
  ids: SyndicationKey[];
  stream?: Nullable<Stream>;
  title: string;
}

interface DropdownBookmarkMentionsEmits {
  "add-new-bookmark-stream": [AddNewBookmarkStreamPayload];
  close: [];
  "move-to-existing-group": [MoveToExistingGroupPayload];
  "update-bookmark-streams": [BookmarkStream];
}

const MAX_TAGGABLE_MENTIONS = 500;

const props = withDefaults(defineProps<DropdownBookmarkMentionsProps>(), {
  stream: null,
});

const emit = defineEmits<DropdownBookmarkMentionsEmits>();

const isMobile = inject<boolean>("isMobile", false);

const streamsStore = useStreamsStore();

const { mediaBookmarkStreams, socialBookmarkStreams } =
  storeToRefs(streamsStore);

const existingGroupId = ref<Maybe<number>>(undefined);
const folders = ref<BookmarkStream[]>([]);
const label = ref<string>("");
const isLoading = ref<boolean>(false);
const mentionsByStream = ref<Record<number, number>>({});
const newGroupColor = ref<string>("silver");
const newGroupLabel = ref<string>("");
const placeIntoNewGroup = ref<boolean>(true);
const showCreateButton = ref<boolean>(true);

const mentions = computed<CompositeIdToMention[]>(() =>
  props.ids.map(compositeIdToMention)
);

const existingBookmarkStreams = computed<Stream[]>(() => {
  const currentStream = props.stream;

  const hasSocialBookmarkStream = Boolean(
    currentStream?.social_bookmarks_stream
  );

  const isSocial = currentStream ? isSocialStream(currentStream) : false;

  const source =
    isSocial || hasSocialBookmarkStream
      ? socialBookmarkStreams.value
      : mediaBookmarkStreams.value;

  return source;
});

const existingGroupInputs = computed<boolean>(
  () => Boolean(label.value) && Boolean(existingGroupId.value)
);

const filteredFolders = computed<BookmarkStream[]>(() =>
  folders.value.filter((folder) =>
    folder.label.toLowerCase().includes(label.value.toLowerCase())
  )
);

const mandatoryFieldsMissing = computed<boolean>(
  () => !newGroupInputs.value && !existingGroupInputs.value
);

const newGroupInputs = computed<boolean>(
  () => Boolean(label.value) && Boolean(newGroupLabel.value)
);

const showSettings = computed<boolean>(
  () =>
    (Boolean(label.value) && !filteredFolders.value.length) ||
    !showCreateButton.value
);

const subTitle = computed<string>(() => {
  if (showSettings.value) {
    return getLocaleText("dropdown_bookmark_mentions.create_a_new_tag");
  }

  return getLocaleText("dropdown_bookmark_mentions.search_or_create_tag");
});

const tagPlaceholder = computed<string>(() => {
  if (showSettings.value) {
    return getLocaleText("dropdown_bookmark_mentions.tab_placeholder");
  }

  return getLocaleText("dropdown_bookmark_mentions.search_tags");
});

const hasTooManyMentions = computed<boolean>(
  () => props.ids.length > MAX_TAGGABLE_MENTIONS
);

function bookmarkColor(bookmarkStream: BookmarkStream): {
  backgroundColor: string;
  color: string;
} {
  const colorPalette: Record<string, BookmarkStreamColor> = {
    ...tagPalette,
    ...defaultBookmarkPalette,
  };

  const color =
    bookmarkStream.color ?? Object.keys(defaultBookmarkPalette)[0] ?? "silver";

  return {
    backgroundColor: color,
    color: colorPalette[color]?.fontColor ?? "white",
  };
}

function buildMentionsByStreamCount(
  response: BookmarkStreamsForMentionsResponse
): void {
  const nextMentionsByStream: Record<number, number> = {};

  Object.values(response.data).forEach((streamIds) => {
    streamIds.forEach((streamId) => {
      nextMentionsByStream[streamId] =
        (nextMentionsByStream[streamId] ?? 0) + 1;
    });
  });

  mentionsByStream.value = nextMentionsByStream;
}

function fetchBookmarkStreamsForMentions(): Promise<BookmarkStreamsForMentionsResponse> {
  return $streemApiV1.post("bookmarks/bookmark_streams_for_mentions", {
    params: {
      mentions: mentions.value.map((mention) => ({
        target_id: mention.id,
        target_type: targetType(mention),
      })),
    },
  });
}

function folderStatus(currentStream: Stream): boolean {
  const totalIncluded = mentionsByStream.value[currentStream.id];

  return totalIncluded === mentions.value.length;
}

async function loadData(): Promise<void> {
  isLoading.value = true;

  try {
    if (!hasTooManyMentions.value) {
      const response = await fetchBookmarkStreamsForMentions();
      buildMentionsByStreamCount(response);
    }

    setupFolders();
  } catch {
    Notify.create({
      message: getLocaleText(
        "dropdown_bookmark_mentions.loading_bookmarks_error"
      ),
    });
  } finally {
    isLoading.value = false;
  }
}

function reset(): void {
  existingGroupId.value = undefined;
  label.value = "";
  newGroupColor.value = "silver";
  newGroupLabel.value = "";

  if (!isMobile) {
    showCreateButton.value = true;
  }
}

function save(): void {
  if (mandatoryFieldsMissing.value) {
    AlertDialog(getLocaleText("alerts.fields_validation"));

    return;
  }

  if (existingGroupId.value) {
    emit("move-to-existing-group", {
      existingGroupId: existingGroupId.value,
      streamLabel: label.value,
    });

    reset();

    return;
  }

  emit("add-new-bookmark-stream", {
    groupColor: newGroupColor.value,
    groupLabel: newGroupLabel.value,
    streamLabel: label.value,
  });

  reset();
}

function setupFolders(): void {
  folders.value = existingBookmarkStreams.value.map((currentStream) => ({
    color: currentStream.color,
    id: currentStream.id,
    label: currentStream.label,
    selected: folderStatus(currentStream),
  }));
}

function showCreateSettings(): void {
  showCreateButton.value = false;
}

function updateBookmarks(bookmarkStream: BookmarkStream): void {
  emit("update-bookmark-streams", bookmarkStream);
}

function cancel(): void {
  if (isMobile) {
    label.value = "";
    showCreateButton.value = true;

    return;
  }

  emit("close");
}

watch(
  existingBookmarkStreams,
  () => {
    loadData();
  },
  { immediate: true }
);
</script>

<style lang="scss" scoped>
.c-dropdown-bookmark-mentions {
  min-width: 300px;
  padding: var(--s-spacing-lg);
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.title {
  font-weight: var(--s-font-weight-bold);
  font-size: var(--s-font-size-lg);
}

.sub-title {
  color: var(--s-color-font-subtext);
}

:deep(.tag) {
  max-width: 35ch;
}

.add-new-bookmark-streem {
  color: var(--s-color-blue-7) !important;
}

.checkboxes {
  max-height: 300px;
  flex: 1;
  overflow: auto;
}

.field-title-max-width {
  :deep(.input-wrapper) {
    input {
      padding-right: 30px;
    }
  }
}

.button-container {
  display: flex;
  justify-content: flex-end;
  gap: var(--s-spacing-md);
}

.error-label {
  font-size: 10px;
  color: var(--s-components-field-error);
}

.create-button {
  --c-button-padding-inline: 0;
}
</style>
